"use client";

import { useEffect, useState } from "react";
import {
  getAuth as getSecondaryAuth,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { ref, update, get, remove } from "firebase/database";
import { auth, db, firebaseConfig } from "../Firebase/firebase-config";
import { initializeApp } from "firebase/app";
import "./PartnerManagement.css";
import { FaUserPlus, FaTrash, FaInfoCircle, FaCheck, FaTimes } from "react-icons/fa";

const secondaryApp = initializeApp(firebaseConfig, "Secondary");
const secondaryAuth = getSecondaryAuth(secondaryApp);

export default function PartnerManagement() {
  const [firms, setFirms] = useState([]);
  const [partners, setPartners] = useState([]);
  const [newPartner, setNewPartner] = useState({ name: "", email: "", password: "" });
  const [selectedFirms, setSelectedFirms] = useState({});
  const [message, setMessage] = useState({ text: "", type: "" }); // success | error
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'add' | 'removePartner' | 'toggleFirm', data: {} }

  const mainUserId = auth.currentUser?.uid;

  // Fetch firms & partners
  useEffect(() => {
    if (!mainUserId) return;

    const fetchData = async () => {
      try {
        const firmsSnap = await get(ref(db, `profileDashboard/${mainUserId}/firms`));
        const firmsData = firmsSnap.val() || {};
        const firmsList = Object.entries(firmsData).map(([id, data]) => ({
          id,
          company_name: data.company_name || "Unnamed Firm",
          gstin: data.gstin || "N/A",
        }));
        setFirms(firmsList);

        const partnersSnap = await get(ref(db, `profileDashboard/${mainUserId}/partners`));
        const partnersList = partnersSnap.exists()
          ? Object.entries(partnersSnap.val()).map(([uid, data]) => ({
            uid,
            ...data,
            assignedFirms: data.assignedFirms || [],
          }))
          : [];
        setPartners(partnersList);
      } catch (err) {
        showMessage("Failed to load data", "error");
      }
    };

    fetchData();
  }, [mainUserId]);

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 5000);
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    if (!newPartner.name.trim() || !newPartner.email.trim() || !newPartner.password) {
      showMessage("Please fill all fields", "error");
      return;
    }

    const selectedIds = Object.keys(selectedFirms).filter((id) => selectedFirms[id]);
    

    setConfirmAction({
      type: "add",
      data: { ...newPartner, firmIds: selectedIds },
    });
  };

  const confirmAddPartner = async () => {
    const { name, email, password, firmIds } = confirmAction.data;

    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email.trim(), password);
      const uid = cred.user.uid;

      const newPartnerData = {
        uid,
        name: name.trim(),
        email: email.trim(),
        assignedFirms: firmIds,
        role: "viewer",
        createdAt: Date.now(),
      };

      // Save to Firebase
      await update(ref(db, `profileDashboard/${mainUserId}/partners/${uid}`), newPartnerData);

      // UPDATE LOCAL STATE IMMEDIATELY → Shows on right side instantly!
      setPartners(prev => [...prev, newPartnerData]);

      showMessage("Partner added successfully!");
      setNewPartner({ name: "", email: "", password: "" });
      setSelectedFirms({});
    } catch (err) {
      const msg =
        err.code === "auth/email-already-in-use"
          ? "This email is already in use"
          : "Failed to create partner account";
      showMessage(msg, "error");
    } finally {
      setConfirmAction(null);
    }
  };
  const toggleFirmAccess = (partnerUid, firmId) => {
    const partner = partners.find((p) => p.uid === partnerUid);
    const hasAccess = partner.assignedFirms.includes(firmId);

    setConfirmAction({
      type: "toggleFirm",
      data: { partnerUid, firmId, hasAccess, partnerName: partner.name },
    });
  };

  const confirmToggleFirm = async () => {
    const { partnerUid, firmId, hasAccess } = confirmAction.data;
    const partner = partners.find((p) => p.uid === partnerUid);
    const newAssigned = hasAccess
      ? partner.assignedFirms.filter((id) => id !== firmId)
      : [...partner.assignedFirms, firmId];

    await update(ref(db, `profileDashboard/${mainUserId}/partners/${partnerUid}`), {
      assignedFirms: newAssigned,
    });

    setPartners((prev) =>
      prev.map((p) =>
        p.uid === partnerUid ? { ...p, assignedFirms: newAssigned } : p
      )
    );

    showMessage(
      hasAccess
        ? "Firm access revoked"
        : "Firm access granted successfully!",
      "success"
    );
    setConfirmAction(null);
  };

  const confirmRemovePartner = async () => {
    const uid = confirmAction.data.uid;
    await remove(ref(db, `profileDashboard/${mainUserId}/partners/${uid}`));
    setPartners((prev) => prev.filter((p) => p.uid !== uid));
    showMessage("Partner removed successfully", "success");
    setConfirmAction(null);
  };

  const partnerInfo = ()=>{
     alert(`
HOW TO ADD A PARTNER (Simple Steps)

1. Enter Partner Name (e.g. Rahul CA)
2. Enter their Email (e.g. rahul@ca.com)
3. Set a Password for them
4. Tick the firms they should see
5. Click "Create Partner Account"

They will get login access immediately!
They can only VIEW Order Status page and they can update Your Stock.
  `.trim())
  }


  return (
    <div className="pm-container">
      <div className="pm-grid">
        {/* Add Partner Card */}
        <div className="pm-card">
          <div className="pm-card-header">
            <h2>
              <FaUserPlus className="pm-icon" /> Add New Partner
              <FaInfoCircle
                className="pm-info-icon"
                title="Create partner account and assign accessible firms"
                onClick={partnerInfo}
              />
            </h2>
          </div>

          <form onSubmit={handleAddPartner} className="pm-form">
            <input
              type="text"
              placeholder="Partner Name"
              value={newPartner.name}
              onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })}
              className="pm-input"
            />
            <input
              type="email"
              placeholder="partner@example.com"
              value={newPartner.email}
              onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })}
              className="pm-input"
              autoComplete="off"
            />
            <input
              type="password"
              placeholder="Password (min 6 chars)"
              value={newPartner.password}
              onChange={(e) => setNewPartner({ ...newPartner, password: e.target.value })}
              className="pm-input"
              autoComplete="new-password"
            />

            <div className="pm-firm-list">
              <label className="pm-label">Assign Firms (Optional)</label>
              {firms.length === 0 ? (
                <p className="pm-empty-text">No firms available</p>
              ) : (
                firms.map((firm) => (
                  <label key={firm.id} className="pm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!selectedFirms[firm.id]}
                      onChange={(e) =>
                        setSelectedFirms({ ...selectedFirms, [firm.id]: e.target.checked })
                      }
                    />
                    <span>
                      <strong>{firm.company_name}</strong>
                    </span>
                  </label>
                ))
              )}
            </div>

            <button type="submit" className="pm-btn pm-btn-primary">
              Create Partner Account
            </button>
          </form>
        </div>

        {/* Partners List */}
        <div className="pm-card pm-partners-card">
          <div className="pm-card-header">
            <h2>Your Partners ({partners.length})</h2>
            <p className="pm-subtitle">Click on a firm to grant/revoke access</p>
          </div>

          <div className="pm-partners-list">
            {partners.length === 0 ? (
              <div className="pm-empty">
                <p>No partners added yet</p>
              </div>
            ) : (
              partners.map((partner) => (
                <div key={partner.uid} className="pm-partner-item">
                  <div className="pm-partner-header">
                    <div>
                      <h3>{partner.name}</h3>
                      <p>{partner.email}</p>
                      <span className="pm-role">Viewer</span>
                    </div>
                    <button
                      onClick={() =>
                        setConfirmAction({ type: "removePartner", data: { uid: partner.uid } })
                      }
                      className="pm-btn pm-btn-danger-sm"
                    >
                      <FaTrash />
                    </button>
                  </div>

                  <div className="pm-firm-access">
                    <strong>Accessible Firms:</strong>
                    <div className="pm-firm-grid">
                      {firms.map((firm) => {
                        const hasAccess = partner.assignedFirms.includes(firm.id);
                        return (
                          <div
                            key={firm.id}
                            className={`pm-firm-tag ${hasAccess ? "active" : ""}`}
                            onClick={() => toggleFirmAccess(partner.uid, firm.id)}
                          >
                            {hasAccess && <FaCheck className="pm-check" />}
                            {!hasAccess && <FaTimes className="pm-cross" />}
                            <span>{firm.company_name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Global Message */}
      {message.text && (
        <div className={`pm-toast ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="pm-modal-overlay">
          <div className="pm-modal">
            <h3>
              {confirmAction.type === "add" && "Create Partner Account?"}
              {confirmAction.type === "removePartner" && "Remove Partner?"}
              {confirmAction.type === "toggleFirm" &&
                (confirmAction.data.hasAccess
                  ? "Revoke Firm Access?"
                  : "Grant Firm Access?")}
            </h3>
            <p>
              {confirmAction.type === "add" &&
    `Add "${confirmAction.data.name}" with access to ${
      confirmAction.data.firmIds.length === 0
        ? "no firms (you can assign later)"
        : confirmAction.data.firmIds.length + " firm(s)"
    }?`}
              {confirmAction.type === "removePartner" && "This action cannot be undone."}
              {confirmAction.type === "toggleFirm" &&
                `${confirmAction.data.hasAccess ? "Remove" : "Grant"} access to "${firms.find((f) => f.id === confirmAction.data.firmId)?.company_name
                }" for ${confirmAction.data.partnerName}?`}
            </p>
            <div className="pm-modal-actions">
              <button
                onClick={() => setConfirmAction(null)}
                className="pm-btn pm-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={
                  confirmAction.type === "add"
                    ? confirmAddPartner
                    : confirmAction.type === "removePartner"
                      ? confirmRemovePartner
                      : confirmToggleFirm
                }
                className={`pm-btn ${confirmAction.type === "removePartner" ? "pm-btn-danger" : "pm-btn-primary"
                  }`}
              >
                {confirmAction.type === "toggleFirm"
                  ? confirmAction.data.hasAccess
                    ? "Revoke"
                    : "Grant"
                  : confirmAction.type === "removePartner"
                    ? "Remove"
                    : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}